<script lang="ts">
  import { onMount } from 'svelte'
  import { api, handleApiError, ApiError } from '../lib/api'
  import { fade, slide } from 'svelte/transition'
  import { addToast } from '../lib/toast'
  
  // Importar componentes de mensajería
  import WhatsAppMessageForm from '../components/messaging/WhatsAppMessageForm.svelte'
  import TelegramMessageForm from '../components/messaging/TelegramMessageForm.svelte'
  import EmailMessageForm from '../components/messaging/EmailMessageForm.svelte'

  type Session = { id: string; label: string; number: string; type?: string; connected?: boolean }
  type Contact = { id: string; session_id: string; phone_number: string; identifier: string }
  
  let sessions: Session[] = []
  let selectedSession: Session | null = null
  let contacts: Contact[] = []
  
  // Combobox / Select Filtrable State para Sesiones
  let isSelectOpen = false
  let selectSearch = ''
  let selectEl: HTMLElement
  
  // Contactos para WhatsApp
  let selectedContact: Contact | null = null
  let showSaveContactModal = false
  let contactIdentifier = ''
  let savingContact = false

  // Filtered sessions for combobox
  $: filteredSessions = sessions.filter(s => 
    s.label.toLowerCase().includes(selectSearch.toLowerCase()) || 
    (s.number && s.number.includes(selectSearch))
  )

  async function loadSessions() {
    try {
      sessions = await api.get('/api/v1/sessions')
      if (sessions.length > 0) {
        selectedSession = sessions[0]
        await loadContacts()
      }
    } catch (e) {
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
      sessions = []
    }
  }

  async function loadContacts() {
    if (!selectedSession) return
    // Solo cargar contactos para WhatsApp
    if (selectedSession.type !== 'whatsapp' && selectedSession.type !== undefined) {
      contacts = []
      return
    }
    try {
      contacts = await api.get(`/api/v1/sessions/${selectedSession.id}/contacts`)
    } catch (e) {
      // Para carga de contactos, solo mostrar toast si no es 404 (contactos vacíos es normal)
      if (e instanceof ApiError && e.status === 404) {
        contacts = []
        return
      }
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
      contacts = []
    }
  }

  function toggleSelect() {
    isSelectOpen = !isSelectOpen
    if (isSelectOpen) {
      setTimeout(() => document.getElementById('session-search')?.focus(), 100)
    } else {
      selectSearch = ''
    }
  }

  async function selectOption(s: Session) {
    selectedSession = s
    isSelectOpen = false
    selectSearch = ''
    await loadContacts()
  }

  // Close select when clicking outside
  function handleClickOutside(event: MouseEvent) {
    if (isSelectOpen && selectEl && !selectEl.contains(event.target as Node)) {
      isSelectOpen = false
      selectSearch = ''
    }
  }

  // Handler para cuando se envía un mensaje desde los componentes
  function handleMessageSent() {
    // Recargar contactos si es necesario
    if (selectedSession) {
      loadContacts()
    }
  }

  // Handler para cuando se selecciona un contacto en WhatsApp
  function handleContactSelected(event: CustomEvent) {
    selectedContact = event.detail
  }

  function handleContactCleared() {
    selectedContact = null
  }

  // Helper para determinar qué componente mostrar (reactivo)
  $: formComponent = (() => {
    if (!selectedSession) return null
    
    const sessionType = selectedSession.type || 'whatsapp'
    
    switch (sessionType) {
      case 'telegram_bot':
      case 'telegram_mtproto':
        return 'telegram'
      case 'email':
        return 'email'
      case 'whatsapp':
      default:
        return 'whatsapp'
    }
  })()

  async function saveContact() {
    if (!selectedSession || !selectedContact) {
      addToast('Por favor, selecciona un contacto primero', 'error')
      return
    }
    
    savingContact = true
    try {
      const contact = await api.post(`/api/v1/sessions/${selectedSession.id}/contacts`, {
        phone_number: selectedContact.phone_number,
        identifier: contactIdentifier || selectedContact.phone_number
      })
      contacts = [...contacts, contact]
      selectedContact = contact
      showSaveContactModal = false
      contactIdentifier = ''
      addToast('Contacto guardado correctamente', 'success')
    } catch (e: any) {
      addToast(e.message || 'Error al guardar contacto', 'error')
    } finally {
      savingContact = false
    }
  }

  onMount(() => {
    loadSessions()
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  })
</script>

<div class="space-y-8">
  <div class="flex flex-col md:flex-row md:items-end justify-between gap-4">
    <div>
      <h2 class="text-4xl font-black text-white tracking-tight mb-2">Mensajería</h2>
      <p class="text-indigo-200/60 font-medium">Envía mensajes y multimedia a tus contactos</p>
    </div>
  </div>

  <!-- Custom Filterable Select (Combobox) -->
  <div class="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm relative z-20">
    <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-3">Sesión</label>
    
    <div class="relative max-w-md" bind:this={selectEl}>
      <button 
        on:click|stopPropagation={toggleSelect}
        class="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-black/30 border border-white/10 text-white hover:border-pink-500/50 hover:bg-black/40 transition-all group"
      >
        {#if selectedSession}
          <div class="flex flex-col items-start">
            <span class="font-bold text-lg">{selectedSession.label}</span>
            <span class="text-xs text-indigo-200/50 font-mono">{selectedSession.number || 'Sin número'}</span>
          </div>
        {:else}
          <span class="text-indigo-200/50">Seleccionar sesión...</span>
        {/if}
        <svg class="w-5 h-5 text-indigo-200/50 group-hover:text-pink-400 transition-colors {isSelectOpen ? 'rotate-180' : ''}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {#if isSelectOpen}
        <div 
          transition:slide={{ duration: 200 }}
          class="absolute top-full left-0 w-full mt-2 bg-[#1a1b26] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div class="p-3 border-b border-white/5">
            <input 
              id="session-search"
              bind:value={selectSearch}
              placeholder="Buscar sesión..."
              class="w-full bg-black/20 border border-white/5 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-pink-500/50 placeholder-white/20"
              on:click|stopPropagation
            />
          </div>
          <div class="max-h-60 overflow-y-auto custom-scrollbar">
            {#if filteredSessions.length === 0}
              <div class="p-4 text-center text-indigo-200/40 text-sm">No se encontraron sesiones</div>
            {:else}
              {#each filteredSessions as s}
                <button 
                  on:click|stopPropagation={() => selectOption(s)}
                  class="w-full text-left px-5 py-3 hover:bg-white/5 transition-colors flex items-center justify-between group"
                >
                  <div>
                    <div class="font-medium text-white group-hover:text-pink-300 transition-colors">{s.label}</div>
                    <div class="text-xs text-indigo-200/40">{s.number || 'Sin número'}</div>
                  </div>
                  {#if selectedSession?.id === s.id}
                    <svg class="w-5 h-5 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  {/if}
                </button>
              {/each}
            {/if}
          </div>
        </div>
      {/if}
    </div>
  </div>

  <!-- Formulario Reactivo según tipo de sesión -->
  <div class="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
    {#if !selectedSession}
      <div class="text-center py-12 text-indigo-200/50">
        <p>Selecciona una sesión para enviar mensajes</p>
      </div>
    {:else if formComponent === 'whatsapp'}
      <WhatsAppMessageForm
        sessionId={selectedSession.id}
        {contacts}
        {selectedContact}
        on:sent={handleMessageSent}
        on:contactSelected={handleContactSelected}
        on:contactCleared={handleContactCleared}
      />
    {:else if formComponent === 'telegram'}
      <TelegramMessageForm
        sessionId={selectedSession.id}
        sessionType={selectedSession.type || 'telegram_bot'}
        on:sent={handleMessageSent}
      />
    {:else if formComponent === 'email'}
      <EmailMessageForm
        sessionId={selectedSession.id}
        on:sent={handleMessageSent}
      />
    {:else}
      <div class="text-center py-12 text-indigo-200/50">
        <p>Tipo de sesión desconocido: {selectedSession.type || 'sin tipo'}</p>
      </div>
    {/if}
  </div>
</div>

<!-- Modal para guardar contacto (solo para WhatsApp) -->
{#if showSaveContactModal && selectedSession && (selectedSession.type === 'whatsapp' || !selectedSession.type)}
  <div 
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    transition:fade
    on:click={() => showSaveContactModal = false}
  >
    <div 
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden"
      transition:slide
      on:click|stopPropagation
    >
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500"></div>
      
      <h3 class="text-2xl font-black text-white mb-6">Guardar Contacto</h3>
      
      <div class="space-y-5">
        <div>
          <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Número</label>
          <input 
            type="text"
            value={selectedContact?.phone_number || ''}
            disabled
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white/60 cursor-not-allowed"
          />
        </div>
        
        <div>
          <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
            Nombre / Identificador <span class="text-indigo-200/40 normal-case font-normal">(Opcional)</span>
          </label>
          <input 
            bind:value={contactIdentifier}
            placeholder="Ej: Juan Pérez, Cliente VIP, etc."
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
            on:keydown={(e) => {
              if (e.key === 'Enter') {
                saveContact()
              }
            }}
          />
          <p class="text-xs text-indigo-200/40 mt-1.5 ml-1">Si no ingresas un nombre, se usará el número como identificador</p>
        </div>
      </div>
      
      <div class="flex justify-end gap-3 mt-8">
        <button 
          on:click={() => {
            showSaveContactModal = false
            contactIdentifier = ''
          }}
          class="px-6 py-3 text-indigo-200/60 hover:text-white transition-colors font-medium"
        >
          Cancelar
        </button>
        <button 
          on:click={saveContact}
          disabled={savingContact}
          class="group px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden"
        >
          <div class="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-700 -skew-x-12 -translate-x-full"></div>
          <span class="relative z-10">{savingContact ? 'Guardando...' : 'Guardar'}</span>
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
  }
</style>
